import { useEffect, useId, useState, useMemo } from "react";
import { ChevronDown, CheckCircle2, Lightbulb, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ENTITY_SIZE_TO_COMPANY_SIZE,
  INCADRARE_FROM_CLASSIFICATION,
  NIVEL_RISC_FROM_CYFUN_LEVEL,
  type CompanySize,
  type Incadrare,
  type NivelRisc,
} from "@/constants/credit";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useGetProducts } from "@/hooks/api/query/useGetProducts";
import { computeJourneyBreakdown } from "@/utils/journeyCredits";

type JourneyCostCalculatorProps = {
  sessionId: string | null | undefined;
  highlightedPackageCredits?: number | null;
  onRecommendedPackage?: (credits: number | null) => void;
};

const EP_EXTRA_CREDITS = 16;

const SIZE_LABELS: Record<CompanySize, string> = {
  S: "MICĂ",
  M: "MIJLOCIE",
  L: "MARE",
};

function resolveCompanySize(rawSize: unknown): CompanySize | null {
  if (typeof rawSize !== "string") return null;
  const trimmed = rawSize.trim();
  if (trimmed === "S" || trimmed === "M" || trimmed === "L") return trimmed;
  const upper = trimmed.toUpperCase();
  return ENTITY_SIZE_TO_COMPANY_SIZE[upper] ?? null;
}

export function JourneyCostCalculator({
  sessionId,
  highlightedPackageCredits,
  onRecommendedPackage,
}: JourneyCostCalculatorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const panelId = useId();

  const { data: entityDataResponse, isLoading } = useGetEntityData(sessionId);
  const { data: productsData } = useGetProducts();

  const entityData = entityDataResponse?.entity_data;
  const entityName = entityData?.nume_entitate ?? null;
  const rawSize = entityData?.entity_size ?? entityData?.dimensiune ?? null;
  const companySize = resolveCompanySize(rawSize);

  const classificationKey =
    entityData?.nis_classification?.classification ?? null;
  const incadrare: Incadrare | null = classificationKey
    ? (INCADRARE_FROM_CLASSIFICATION[classificationKey] ?? null)
    : null;

  const cyfunLevel = (entityData as { nivel_risc?: string } | undefined)
    ?.nivel_risc;
  const nivelRisc: NivelRisc | null = cyfunLevel
    ? (NIVEL_RISC_FROM_CYFUN_LEVEL[cyfunLevel] ?? null)
    : null;

  const isSmallEssential =
    companySize === "S" && classificationKey === "ESSENTIAL";

  const breakdown = useMemo(() => {
    if (!companySize || !incadrare || !nivelRisc) return null;
    return computeJourneyBreakdown(
      { size: companySize, incadrare, nivelRisc },
      productsData?.products ?? []
    );
  }, [companySize, incadrare, nivelRisc, productsData]);

  // Keep parent highlight in sync with breakdown — fires as soon as entity
  // data resolves, and re-fires if the entity (size) changes mid-session.
  useEffect(() => {
    if (!onRecommendedPackage) return;
    onRecommendedPackage(breakdown?.recommendedPackageCredits ?? null);
  }, [breakdown, onRecommendedPackage]);

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-3 py-2 md:px-4 md:py-3 text-left group hover:bg-blue-50/70 transition-colors"
        aria-expanded={isExpanded}
        aria-controls={panelId}
      >
        <div className="flex items-center gap-2.5">
          <Lightbulb className="w-4 h-4 text-blue-500 shrink-0" />
          <span className="text-xs md:text-sm font-medium text-blue-700">
            Vezi câte credite îți trebuie pentru conformitatea NIS2
          </span>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-blue-400 transition-transform duration-200 shrink-0",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {isExpanded && (
        <div
          id={panelId}
          role="region"
          aria-label="Detalii credite conformitate NIS2"
          className="border-t border-blue-100 px-3 pb-3 pt-2 md:px-4 md:pb-4 md:pt-3"
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs md:text-sm text-slate-500 py-1.5 md:py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Calculăm costul pentru dimensiunea ta...</span>
            </div>
          ) : !companySize || !breakdown ? (
            <div className="text-xs md:text-sm text-slate-500 py-1.5 md:py-2">
              <p className="font-medium mb-1">
                Finalizați evaluarea de risc pentru a vedea costul total
                personalizat.
              </p>
            </div>
          ) : (
            <div>
              {/* Header */}
              <p className="text-xs md:text-sm font-semibold text-slate-700 mb-2 md:mb-3">
                {entityName ? (
                  <>
                    Pentru <span className="text-slate-900">{entityName}</span>
                    {" — "}
                    <span className="text-blue-700">
                      Entitate {SIZE_LABELS[companySize]}
                    </span>
                  </>
                ) : (
                  <>
                    Entitate{" "}
                    <span className="text-blue-700">
                      {SIZE_LABELS[companySize]}
                    </span>
                  </>
                )}
              </p>

              {/* Journey stages — Plan de Remediere is inline with the rest */}
              <div className="space-y-1.5 md:space-y-2 mb-2 md:mb-3">
                {[breakdown.stage1, breakdown.stage2, breakdown.stage3].map(
                  (stage) => (
                    <div
                      key={stage.label}
                      className="flex items-center justify-between text-xs md:text-sm gap-3"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        <span className="text-slate-700">{stage.label}</span>
                      </div>
                      <span className="font-semibold text-slate-800 tabular-nums shrink-0">
                        {stage.total} credite
                      </span>
                    </div>
                  )
                )}

                {isSmallEssential && (
                  <div className="flex items-center justify-between text-xs md:text-sm gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="text-slate-600 italic">
                        + Efect Perturbator (EP)
                      </span>
                    </div>
                    <span className="font-semibold text-slate-700 tabular-nums shrink-0">
                      +{EP_EXTRA_CREDITS} credite
                    </span>
                  </div>
                )}

                {/* Plan de Remediere — only shown for ESENȚIALE (Imp = unavailable, total = 0) */}
                {breakdown.planRemediere.total > 0 && (
                  <div className="flex items-center justify-between text-xs md:text-sm gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
                      <span className="text-slate-700">
                        {breakdown.planRemediere.label}
                      </span>
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded uppercase tracking-wider leading-none">
                        În așteptare DNSC
                      </span>
                    </div>
                    <span className="font-semibold text-slate-800 tabular-nums shrink-0">
                      {breakdown.planRemediere.total} credite
                    </span>
                  </div>
                )}
              </div>

              {/* Divider + total */}
              <div className="border-t border-slate-200 pt-2 md:pt-2.5 mb-2 md:mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm font-bold text-slate-800">
                    Total călătorie NIS2
                  </span>
                  <span className="text-base font-extrabold text-slate-900 tabular-nums">
                    {breakdown.totalWithRemediere}
                    {isSmallEssential ? `+${EP_EXTRA_CREDITS}` : ""} credite
                  </span>
                </div>
              </div>

              {/* Smart nudge */}
              {breakdown.recommendedPackageCredits != null && (
                <div
                  className={cn(
                    "rounded-xl p-2.5 md:p-3 flex items-start gap-2 md:gap-2.5",
                    highlightedPackageCredits ===
                      breakdown.recommendedPackageCredits
                      ? "bg-green-100 border border-green-300"
                      : "bg-green-50 border border-green-200"
                  )}
                >
                  <span className="text-base leading-none mt-0.5">💡</span>
                  <p className="text-xs md:text-sm text-green-800 font-medium leading-snug">
                    Pachetul de{" "}
                    <span className="font-extrabold">
                      {breakdown.recommendedPackageCredits} credite
                    </span>{" "}
                    acoperă etapele obligatorii de acum. Planul de Remediere va
                    necesita credite suplimentare la activarea DNSC.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
